import React from 'react';
import 'dayjs/locale/es';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { IconButton, Stack, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import dayjs, { Dayjs } from 'dayjs';

interface DateRangePickerProps {
    startDate?: Dayjs | Date | string | null;
    endDate?: Dayjs | Date | string | null;
    onChange: (startDate: Date | null, endDate: Date | null) => void;
    adapterLocale?: string;
    title?: string;
    startLabel?: string;
    endLabel?: string;
}

const DateRangePickerComponent: React.FC<DateRangePickerProps> = ({
    startDate,
    endDate,
    onChange,
    adapterLocale = 'es',
    title,
    startLabel = 'Fecha de Inicio',
    endLabel = 'Fecha de Fin'
}) => {
    const today = dayjs();

    const parsedStartDate = startDate && dayjs(startDate).isValid() ? dayjs(startDate) : null;
    const parsedEndDate = endDate && dayjs(endDate).isValid() ? dayjs(endDate) : null;

    const handleStartDateChange = (date: Dayjs | null) => {
        const validDate = date && dayjs(date).isValid() ? date.toDate() : null;
        const end = parsedEndDate ? parsedEndDate.toDate() : null;

        onChange(validDate, end);
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        const validDate = date && dayjs(date).isValid() ? date.toDate() : null;
        const start = parsedStartDate ? parsedStartDate.toDate() : null;

        onChange(start, validDate);
    };

    const clearStartDate = () => {
        const end = parsedEndDate ? parsedEndDate.toDate() : null;
        onChange(null, end);
    };

    const clearEndDate = () => {
        const start = parsedStartDate ? parsedStartDate.toDate() : null;
        onChange(start, null);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
            <Stack spacing={0.5} width="100%">
                <Stack direction="row" spacing={2} alignItems="center">
                    <Stack position="relative" flex={1}>
                        <DatePicker
                            label={!title ? startLabel : undefined}
                            value={parsedStartDate}
                            onChange={handleStartDateChange}
                            disableFuture
                            maxDate={parsedEndDate || today}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: title ? startLabel : undefined,
                                    inputProps: { 'data-testid': 'range-startDate' }
                                }
                            }}
                        />

                        {parsedStartDate && (
                            <IconButton
                                onClick={clearStartDate}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 40
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>

                    <Stack position="relative" flex={1}>
                        <DatePicker
                            label={!title ? endLabel : undefined}
                            value={parsedEndDate}
                            onChange={handleEndDateChange}
                            disableFuture
                            minDate={parsedStartDate || undefined}
                            maxDate={today}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: title ? endLabel : undefined,
                                    inputProps: { 'data-testid': 'range-endDate' }
                                }
                            }}
                        />

                        {parsedEndDate && (
                            <IconButton
                                onClick={clearEndDate}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 40
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </LocalizationProvider>
    );
};

export default DateRangePickerComponent;
